import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { chatsApi } from '../../services/chats/chats'
import type { RootState } from '../../store'
import type { IPagination } from '../../types'
import type { IChat, IMessage } from '../../types/chats'

interface ChatState {
    activeChat: IChat | null
    messages: IMessage[]
    editMessage: IMessage | null
    pagination: IPagination | undefined
    socket: any
    allChats: IChat[] | []
}

const initialState: ChatState = {
    activeChat: null,
    messages: [],
    editMessage: null,
    pagination: undefined,
    socket: null,
    allChats: [],
}

const chatSlice = createSlice({
    name: 'chats',
    initialState,
    reducers: {
        setChatById: (state, action) => {
            if (action.payload.id === state.activeChat?.id) {
                state.activeChat = action.payload
            }
            state.allChats = state.allChats.map((chat) => {
                if (chat.id === action.payload.id) {
                    return {
                        ...chat,
                        ...action.payload,
                    }
                }
                return chat
            })
        },
        setActiveChat(state, action: PayloadAction<IChat | null>) {
            state.activeChat = action.payload
            state.messages = []
        },
        setSocket: (state, action) => {
            state.socket = action.payload
        },
        setNewMessage: (state, action) => {
            if (action.payload.chatId === state.activeChat?.id) {
                state.messages.push(action.payload)
            }
            if (state.allChats?.length) {
                state.allChats = state?.allChats?.map((chat) =>
                    chat.id === action.payload.chatId
                        ? {
                              ...chat,
                              lastMessage: action.payload,
                              unreadMessagesCount: chat.unreadMessagesCount + 1,
                          }
                        : chat,
                )
            }
        },
        setEditMessage: (state, action) => {
            state.editMessage = action.payload
        },
        setUpdateMessage: (state, action) => {
            console.log('action.payload', action.payload)
            // state.editMessage = action.payload
        },
        setRemoveMessage: (state, action) => {
            if (action.payload?.id) {
                state.messages = state.messages.filter(
                    (message) => message.id !== action.payload.id,
                )
            }
        },
        setRemoveChat: (state, action) => {
            if (action.payload?.id && state?.allChats) {
                state.allChats = state?.allChats?.filter(
                    (chat) => chat.id !== action.payload.id,
                )
            }
        },
        setNewChat: (state, action) => {
            if (action.payload) {
                state.allChats = [...(state?.allChats || []), action.payload]
            }
        },
        // readMessages: (state, action: PayloadAction<IMessage[]>) => {
        //     state.messages = state.messages.map((msg) => ({
        //         ...msg,
        //         isRead: action.payload.some((m) => m.id === msg.id) ? true : msg.isRead,
        //     }))
        // },
        setChatUserStatus: (
            state,
            action: PayloadAction<{
                id: number
                status: 'online' | 'offline'
                lastSeen: string
            }>,
        ) => {
            if (!state.allChats?.length) return
            for (const chat of state.allChats) {
                const user = chat.users.find((u) => u.id === action.payload.id)
                if (user) {
                    const isOnline = action.payload.status === 'online'
                    if (isOnline) user.status = action.payload.status
                    user.status = action.payload.lastSeen
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                chatsApi.endpoints.getChatById.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data) {
                        let messagesTemp
                        const newMessages = [...payload.data.messages].reverse()
                        if (state?.activeChat?.id !== payload.data.id) {
                            messagesTemp = newMessages
                        } else {
                            messagesTemp = new Map(
                                [...newMessages, ...state.messages].map(
                                    (msg) => [msg.id, msg],
                                ),
                            )
                        }
                        state.activeChat = payload.data
                        state.pagination = payload?.pagination
                        state.messages = Array.from(messagesTemp.values())
                    } else {
                        state.activeChat = null
                        state.messages = []
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.updateChat.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data) {
                        const chatIndex = state?.allChats?.findIndex(
                            (chat) => chat.id === payload.data.id,
                        )
                        if (chatIndex && chatIndex > -1 && state.allChats) {
                            state.allChats[chatIndex] = {
                                ...state.allChats[chatIndex],
                                ...payload.data,
                            }
                        }
                        if (state?.activeChat?.id === payload.data.id) {
                            state.activeChat = {
                                ...state.activeChat,
                                ...payload.data,
                            }
                        }
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.getAllChats.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data) {
                        if (
                            state?.allChats &&
                            payload?.pagination &&
                            payload?.pagination?.page > 1
                        ) {
                            const chatsTemp = new Map(
                                [...payload.data, ...state.allChats].map(
                                    (chat) => [chat.id, chat],
                                ),
                            )
                            state.allChats = Array.from(
                                chatsTemp.values(),
                            ) as IChat[]
                        } else {
                            state.allChats = payload.data
                        }
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.sendMessage.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data) {
                        state.messages.push(payload.data)
                        if (state?.allChats) {
                            state.allChats = state?.allChats?.map((chat) =>
                                chat.id === payload.data.chatId
                                    ? { ...chat, lastMessage: payload.data }
                                    : chat,
                            )
                        }
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.removeMessage.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data?.id) {
                        state.messages = state.messages.filter(
                            (message) => message.id !== payload.data.id,
                        )
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.removeChat.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data?.id) {
                        state.allChats =
                            state?.allChats?.filter(
                                (one) => one.id !== payload.data.id,
                            ) || []
                        state.activeChat = null
                        state.messages = []
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.leaveChat.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data?.id) {
                        state.allChats =
                            state?.allChats?.filter(
                                (one) => one.id !== payload.data.id,
                            ) || []
                        state.activeChat = null
                        state.messages = []
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.marcUsRead.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data) {
                        console.log('state.allChats', state.allChats)
                        state.allChats = state.allChats.map((chat) => ({
                            ...chat,
                            unreadMessagesCount: payload.data.some(
                                (m) => m.id === chat.lastMessage!.id,
                            )
                                ? chat.unreadMessagesCount - 1
                                : chat.unreadMessagesCount,
                            lastMessage: chat.lastMessage
                                ? {
                                      ...chat.lastMessage,
                                      isRead:
                                          payload.data.some(
                                              (m) =>
                                                  m.id === chat.lastMessage!.id,
                                          ) || chat.lastMessage.isRead,
                                  }
                                : chat.lastMessage,
                        }))

                        state.messages = state.messages.map((msg) => ({
                            ...msg,
                            isRead:
                                payload.data.some((m) => m.id === msg.id) ||
                                msg.isRead,
                        }))

                        console.log('state.allChats', state.allChats)
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.updateChat.matchFulfilled,
                (state, { payload }) => {
                    if (payload.data && state.activeChat) {
                        const editedChat = {
                            ...state.activeChat,
                            users: payload.data.users,
                            name: payload.data.name,
                        }
                        state.activeChat = editedChat
                        if (state?.allChats?.length) {
                            const index = state.allChats.findIndex(
                                (chat) => chat.id === payload.data.id,
                            )
                            if (index > -1) {
                                state.allChats[index] = editedChat
                            }
                        }
                    }
                },
            )
            .addMatcher(
                chatsApi.endpoints.createChatByUser.matchFulfilled,
                (state, { payload }) => {
                    if (
                        payload.data &&
                        state?.allChats?.findIndex(
                            (chat) => chat.id === payload.data.id,
                        ) === -1
                    ) {
                        state.allChats = [
                            ...(state?.allChats || []),
                            payload.data,
                        ]
                    }
                },
            )
    },
})

export const {
    setActiveChat,
    setEditMessage,
    setSocket,
    setNewMessage,
    setUpdateMessage,
    setRemoveMessage,
    setRemoveChat,
    setNewChat,
    setChatUserStatus,
    setChatById,
    // readMessages,
} = chatSlice.actions

export const selectActiveChat = (state: RootState) => state.chat?.activeChat
export const selectMessages = (state: RootState) => state.chat.messages
export const selectPagination = (state: RootState) => state.chat.pagination
export const getSocket = (state: RootState) => state.chat.socket
export const getEditMessage = (state: RootState) => state.chat.editMessage
export const getAllChats = (state: RootState) => state.chat.allChats

export default chatSlice.reducer
